import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/**
 * Adds a thin horizontal scrollbar above a wide table, kept in sync with the table's own scroll.
 * Mirrors whichever element actually overflows: the host, or a PrimeNG `.p-datatable-wrapper` inside it.
 */
@Directive({ selector: '[appTopScrollbar]' })
export class TopScrollbarDirective implements AfterViewInit, OnDestroy {
  /** Optional CSS selector (inside the host) for the element that scrolls horizontally. */
  @Input() appTopScrollbar: string | '' = '';

  private bar?: HTMLDivElement;
  private spacer?: HTMLDivElement;
  private target: HTMLElement | null = null;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private frame = 0;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const hostEl = this.host.nativeElement;
      this.bar = document.createElement('div');
      this.bar.className = 'pp-top-scrollbar';
      this.bar.setAttribute('aria-hidden', 'true');
      this.spacer = document.createElement('div');
      this.spacer.className = 'pp-top-scrollbar__spacer';
      this.bar.appendChild(this.spacer);
      hostEl.parentNode?.insertBefore(this.bar, hostEl);
      this.bar.addEventListener('scroll', this.onBarScroll, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        this.resizeObserver.observe(hostEl);
      }
      this.mutationObserver = new MutationObserver(() => this.schedule());
      this.mutationObserver.observe(hostEl, { childList: true, subtree: true });
      window.addEventListener('resize', this.schedule);
      this.schedule();
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    window.removeEventListener('resize', this.schedule);
    this.bar?.removeEventListener('scroll', this.onBarScroll);
    this.target?.removeEventListener('scroll', this.onTargetScroll);
    this.bar?.remove();
  }

  private schedule = (): void => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  };

  private resolveTarget(): HTMLElement {
    const hostEl = this.host.nativeElement;
    const candidates: (HTMLElement | null)[] = [
      this.appTopScrollbar ? hostEl.querySelector<HTMLElement>(this.appTopScrollbar) : null,
      hostEl.querySelector<HTMLElement>('.p-datatable-wrapper'),
      hostEl,
    ];
    const present = candidates.filter((c): c is HTMLElement => !!c);
    // An element that is wider than its box but has overflow-x: visible never scrolls itself.
    const scrollable = present.filter(c => /^(auto|scroll)$/.test(getComputedStyle(c).overflowX));
    return scrollable.find(c => c.scrollWidth > c.clientWidth + 1) || scrollable[0] || hostEl;
  }

  private update(): void {
    if (!this.bar || !this.spacer) return;
    const next = this.resolveTarget();
    if (next !== this.target) {
      this.target?.removeEventListener('scroll', this.onTargetScroll);
      this.target = next;
      this.target.addEventListener('scroll', this.onTargetScroll, { passive: true });
      if (this.resizeObserver) {
        this.resizeObserver.observe(this.target);
        const table = this.target.querySelector('table');
        if (table) this.resizeObserver.observe(table);
      }
    }
    const overflow = this.target.scrollWidth > this.target.clientWidth + 1;
    this.bar.style.display = overflow ? '' : 'none';
    if (!overflow) return;
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const targetRect = this.target.getBoundingClientRect();
    this.bar.style.marginLeft = `${Math.max(0, targetRect.left - hostRect.left)}px`;
    this.bar.style.width = `${this.target.clientWidth}px`;
    this.spacer.style.width = `${this.target.scrollWidth}px`;
    this.onTargetScroll();
  }

  private onBarScroll = (): void => {
    if (this.target && this.bar && Math.abs(this.target.scrollLeft - this.bar.scrollLeft) >= 1) {
      this.target.scrollLeft = this.bar.scrollLeft;
    }
  };

  private onTargetScroll = (): void => {
    if (this.target && this.bar && Math.abs(this.target.scrollLeft - this.bar.scrollLeft) >= 1) {
      this.bar.scrollLeft = this.target.scrollLeft;
    }
  };
}
